import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { 
  ArrowLeft, 
  Minus, 
  Plus, 
  ShoppingCart, 
  Share2, 
  Star, 
  Check,
  Facebook,
  Twitter,
  MessageCircle,
  Heart
} from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useWishlist } from '../context/WishlistContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const PLACEHOLDER_IMG = 'https://images.unsplash.com/photo-1672736810868-130c00ef5a9e?w=600&q=80';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ProductDetailPage = () => {
  const { id } = useParams();
  const { addItem } = useCart();
  const { isInWishlist, toggleItem } = useWishlist();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(0);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const productRes = await axios.get(`${API}/products/${id}`);
        setProduct(productRes.data);
      } catch (error) {
        console.error('Error fetching product:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  const handleAddToCart = () => {
    if (product && product.stock >= quantity) {
      addItem(product, quantity);
      toast.success(`${quantity} x ${product.name} added to cart!`);
    }
  };

  const handleToggleWishlist = () => {
    if (product) {
      const added = toggleItem(product);
      if (added) {
        toast.success(`${product.name} added to wishlist!`);
      } else {
        toast.success(`${product.name} removed from wishlist`);
      }
    }
  };

  const handleShare = (platform) => {
    const shareUrl = window.location.href;
    const shareText = `Check out ${product?.name} from Reggae Essences!`;

    const urls = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
      twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`,
      whatsapp: `https://wa.me/?text=${encodeURIComponent(shareText + ' ' + shareUrl)}`,
    };

    if (platform === 'copy') {
      navigator.clipboard.writeText(shareUrl);
      toast.success('Link copied to clipboard!');
    } else {
      window.open(urls[platform], '_blank', 'width=600,height=400');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-28 pb-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="animate-pulse grid lg:grid-cols-2 gap-16">
            <div className="aspect-square bg-gray-200 rounded-3xl" />
            <div className="space-y-4">
              <div className="h-8 bg-gray-200 rounded w-3/4" />
              <div className="h-4 bg-gray-200 rounded w-1/2" />
              <div className="h-32 bg-gray-200 rounded" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen pt-28 pb-16 flex items-center justify-center">
        <div className="text-center">
          <h2 className="font-heading text-2xl text-forest mb-4">Product not found</h2>
          <Link to="/shop">
            <Button className="bg-forest text-white rounded-full">
              Back to Shop
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const formatCategory = (category) => {
    return category.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  return (
    <div data-testid="product-detail-page" className="min-h-screen pt-28 pb-16">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Back Button */}
        <Link
          to="/shop"
          className="inline-flex items-center gap-2 text-gray-600 hover:text-forest mb-8 transition-colors"
          data-testid="back-to-shop"
        >
          <ArrowLeft className="h-5 w-5" />
          Back to Shop
        </Link>

        <div className="grid lg:grid-cols-2 gap-16">
          {/* Images */}
          <div className="space-y-4">
            <div className="aspect-square rounded-3xl overflow-hidden bg-sand">
              <img
                src={product.images[selectedImage] || PLACEHOLDER_IMG}
                alt={product.name}
                className="w-full h-full object-cover"
                data-testid="product-main-image"
                onError={(e) => { e.target.onerror = null; e.target.src = PLACEHOLDER_IMG; }}
              />
            </div>
            {product.images.length > 1 && (
              <div className="flex gap-4">
                {product.images.map((img, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedImage(index)}
                    className={`w-20 h-20 rounded-xl overflow-hidden border-2 transition-colors ${
                      selectedImage === index ? 'border-forest' : 'border-transparent'
                    }`}
                  >
                    <img src={img} alt="" className="w-full h-full object-cover" onError={(e) => { e.target.onerror = null; e.target.src = PLACEHOLDER_IMG; }} />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Details */}
          <div className="lg:sticky lg:top-32 lg:self-start">
            <span className="text-coral font-mono text-sm uppercase tracking-wider">
              {formatCategory(product.category)}
            </span>
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-forest mt-2 mb-4">
              {product.name}
            </h1>

            {/* Price */}
            <div className="price-tag text-3xl font-bold text-forest mb-6">
              ${product.price.toFixed(2)}
            </div>

            {/* Description */}
            <p className="text-gray-600 mb-6">{product.description}</p>

            {/* Stock Status */}
            <div className="flex items-center gap-2 mb-6" data-testid="stock-status">
              {product.stock > 0 ? (
                <>
                  <Check className="h-5 w-5 text-green-500" />
                  <span className="text-green-600">
                    {product.stock > 10 ? 'In Stock' : `Only ${product.stock} left!`}
                  </span>
                </>
              ) : (
                <span className="text-red-500 font-medium">Out of Stock</span>
              )}
            </div>

            {/* Quantity & Add to Cart */}
            {product.stock > 0 && (
              <div className="flex items-center gap-4 mb-8">
                <div className="flex items-center border border-gray-200 rounded-full">
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="p-3 hover:bg-gray-100 rounded-l-full"
                    data-testid="quantity-decrease"
                  >
                    <Minus className="h-5 w-5" />
                  </button>
                  <span className="w-12 text-center font-medium" data-testid="quantity-display">
                    {quantity}
                  </span>
                  <button
                    onClick={() => setQuantity(Math.min(product.stock, quantity + 1))}
                    className="p-3 hover:bg-gray-100 rounded-r-full"
                    data-testid="quantity-increase"
                  >
                    <Plus className="h-5 w-5" />
                  </button>
                </div>
                <Button
                  onClick={handleAddToCart}
                  className="flex-1 bg-forest hover:bg-forest-light text-white py-6 rounded-full"
                  data-testid="add-to-cart-btn"
                >
                  <ShoppingCart className="h-5 w-5 mr-2" />
                  Add to Cart
                </Button>
                <Button
                  onClick={handleToggleWishlist}
                  variant="outline"
                  className={`py-6 px-4 rounded-full border-2 transition-colors ${
                    isInWishlist(product.id)
                      ? 'bg-red-50 border-red-500 text-red-500'
                      : 'border-gray-200 hover:border-red-300 hover:text-red-500'
                  }`}
                  data-testid="wishlist-btn"
                >
                  <Heart className={`h-5 w-5 ${isInWishlist(product.id) ? 'fill-red-500' : ''}`} />
                </Button>
              </div>
            )}

            {/* Share */}
            <div className="border-t pt-6">
              <span className="text-sm text-gray-500 mb-3 block">Share this product:</span>
              <div className="flex gap-3">
                <button
                  onClick={() => handleShare('facebook')}
                  className="p-3 bg-[#1877F2] text-white rounded-full hover:opacity-90"
                  data-testid="share-facebook"
                >
                  <Facebook className="h-5 w-5" />
                </button>
                <button
                  onClick={() => handleShare('twitter')}
                  className="p-3 bg-[#1DA1F2] text-white rounded-full hover:opacity-90"
                  data-testid="share-twitter"
                >
                  <Twitter className="h-5 w-5" />
                </button>
                <button
                  onClick={() => handleShare('whatsapp')}
                  className="p-3 bg-[#25D366] text-white rounded-full hover:opacity-90"
                  data-testid="share-whatsapp"
                >
                  <MessageCircle className="h-5 w-5" />
                </button>
                <button
                  onClick={() => handleShare('copy')}
                  className="p-3 bg-gray-200 text-gray-700 rounded-full hover:bg-gray-300"
                  data-testid="share-copy"
                >
                  <Share2 className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Product Info */}
            {(product.ingredients || product.usage_instructions) && (
              <div className="border-t mt-6 pt-6 space-y-4">
                {product.ingredients && (
                  <div>
                    <h4 className="font-semibold text-forest mb-2">Ingredients</h4>
                    <p className="text-gray-600 text-sm">{product.ingredients}</p>
                  </div>
                )}
                {product.usage_instructions && (
                  <div>
                    <h4 className="font-semibold text-forest mb-2">How to Use</h4>
                    <p className="text-gray-600 text-sm">{product.usage_instructions}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Videos Section */}
        {product.videos && product.videos.length > 0 && (
          <div className="mt-16">
            <h2 className="font-heading text-2xl font-bold text-forest mb-6">
              Product Videos
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              {product.videos.map((video, index) => (
                <div key={index} className="aspect-video rounded-2xl overflow-hidden bg-black">
                  <iframe
                    src={video}
                    title={`Product video ${index + 1}`}
                    className="w-full h-full"
                    allowFullScreen
                  />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
